import React from 'react';
import { HorizontalBar } from 'react-chartjs-2';
import { NoGraphDataSvg, LoadingIndicatorSvg, CalendarSvg } from 'components/icons';
import { getDay } from 'date-fns';
import { getIsMobile } from 'utils';

const isMobile = getIsMobile();

const calculateXAxis = (maxDataValue) => {
  let step = 1;
  while (step * 4 < maxDataValue) {
    step += step % 10 === 0 ? 10 : 5;
  }

  const maxY = step * 4;
  return { maxY, step };
}

let weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const CustomChart = (props) => {
  const { completedInspectionsStats, hideCalendar, hideGraph, loadingCompletedInspectionsStats } = props;

  const daysOfWeek = Array.from({ length: 7 }, () => []);

  completedInspectionsStats.filter(i => !!i.completion_date).forEach(i => {
    const date = new Date(i.completion_date);
    let dayIndex = getDay(date);
    dayIndex = (dayIndex + 6) % 7;
    daysOfWeek[dayIndex]?.push(i);
  });

  let maxCount = 0;
  let maxDayIndex = 0;

  daysOfWeek.forEach((dayArray, index) => {
    if (dayArray.length > maxCount) {
      maxCount = dayArray.length;
      maxDayIndex = index;
    }
  });

  var barOptions_stacked = {

    tooltips: {
      // Disable the on-canvas tooltip
      enabled: false,

      custom: function (tooltipModel) {
        // Tooltip Element
        var tooltipEl = document.getElementById('chartjs-tooltip');

        // Create element on first render
        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'chartjs-tooltip';
          tooltipEl.innerHTML = '<table></table>';
          document.body.appendChild(tooltipEl);
        }

        // Hide if no tooltip
        if (tooltipModel.opacity === 0) {
          tooltipEl.style.opacity = 0;
          return;
        }

        // Set caret Position
        tooltipEl.classList.remove('above', 'below', 'no-transform');
        if (tooltipModel.yAlign) {
          tooltipEl.classList.add(tooltipModel.yAlign);
        } else {
          tooltipEl.classList.add('no-transform');
        }

        function getBody(bodyItem, i) {
          let entities = ['Technicians: ', 'Admins: ', 'Residents: ']
          return bodyItem.lines.map(line => {
            return `${entities[i]} ${line}`
          });
        }

        // Set Text
        if (tooltipModel.body) {
          var titleLines = tooltipModel.title || [];
          var bodyLines = tooltipModel.body.map(getBody);

          var innerHtml = '<thead>';

          titleLines.forEach(function (title) {
            let titles = {
              'MON': 'Monday',
              'TUES': 'Tuesday',
              'WED': 'Wednesday',
              'THU': 'Thursday',
              'FRI': 'Friday',
              'SAT': 'Saturday',
              'SUN': 'Sunday',
            };

            innerHtml += '<tr><th></th><th style=\'color: #FFFFFF; font-size: 12px;\'>Inspections completed on ' + titles[title] + '</th></tr>';
          });
          innerHtml += '</thead><tbody>';

          bodyLines.forEach(function (body, i) {
            innerHtml += `<tr><td><div style='border: 1px solid #FFFFFF; background: ${body?.[0]?.includes('Technicians') ? '#04333B' : body?.[0]?.includes('Admins') ? '#1DBD6B' : '#A5E5C4'}; width: 8px; height: 8px; border-radius: 4px; margin-right: 4px;'/></td><td style='color: #C2CCCE; font-size: 12px;'><div style='display: flex; justify-content: space-between; ' ><div>${body?.[0]?.split(':')?.[0]}</div><div>${body?.[0]?.split(':')?.[1]}</div></div></td></tr>`;
          });
          innerHtml += '</tbody>';

          var tableRoot = tooltipEl.querySelector('table');
          tableRoot.innerHTML = innerHtml;
        }

        // `this` will be the overall tooltip
        var position = this._chart.canvas.getBoundingClientRect();

        // Display, position, and set styles for font
        tooltipEl.style.opacity = 1;
        tooltipEl.style.borderRadius = '6px';
        tooltipEl.style.background = '#04333B';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.zIndex = 10;
        tooltipEl.style.padding = '5px 12px';
        tooltipEl.style.left = isMobile ? '40px' : position.left + window.pageXOffset + tooltipModel.caretX + 'px';
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
        tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
        tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
        tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
        tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
        tooltipEl.style.pointerEvents = 'none';
      }
    },

    responsive: true,
    maintainAspectRatio: false,
    scales: {
      xAxes: [{
        barPercentage: 1,
        ticks: {
          beginAtZero: true,
          fontColor: "#A2A3A5",
          fontSize: 11,
          stepSize: calculateXAxis(maxCount).step,
          suggestedMax: calculateXAxis(maxCount).maxY,
        },
        scaleLabel: {
          display: false
        },
        gridLines: {
        },
        stacked: true
      }],
      yAxes: [{
        gridLines: {
          display: false,
          color: "#fff",
          zeroLineColor: "#fff",
          zeroLineWidth: 0
        },
        ticks: {
          fontColor: "#A2A3A5",
          fontFamily: "'Open Sans Bold', sans-serif",
          fontSize: 11
        },
        stacked: true
      }]
    },
    legend: {
      display: false
    },

  };

  let data = {
    labels: ["MON", "TUES", "WED", "THU", "FRI", "SAT", "SUN"],

    datasets: [{
      data: daysOfWeek.map(inspections => inspections.filter(i => i.completed_by_type === 'tech').length),
      backgroundColor: "#04333B",
      hoverBackgroundColor: "#040F12",
      barThickness: 20,
      borderRadius: 6,
    }, {
      data: daysOfWeek.map(inspections => inspections.filter(i => i.completed_by_type === 'admin').length),
      backgroundColor: "#1DBD6B",
      hoverBackgroundColor: "#093920",
      barThickness: 20,
      borderRadius: 6,
    }, {
      data: daysOfWeek.map(inspections => inspections.filter(i => i.completed_by_type === 'tenant').length),
      backgroundColor: "#A5E5C4",
      hoverBackgroundColor: "#117140",
      barThickness: 20,
      borderRadius: 6,
    }]
  };


  const datasetKeyProvider = () => {
    return btoa(Math.random()).substring(0, 12)
  }

  return (
    <div className={isMobile ? "w-full relative flex flex-col items-center justify-center" : "w-full relative flex items-center"} style={{ height: 340 }}>
      {!hideGraph && !loadingCompletedInspectionsStats &&
        <div className='mr-10 w-full h-full relative'>
          <HorizontalBar data={data} options={barOptions_stacked} datasetKeyProvider={datasetKeyProvider} />
          {completedInspectionsStats?.length === 0 &&
            <div className='absolute flex left-0 right-0 top-0 bottom-0 items-center justify-center'>
              <NoGraphDataSvg inspectionsAnalytics />
            </div>
          }
        </div>
      }
      {!hideCalendar && !loadingCompletedInspectionsStats &&
        <div className='flex flex-col items-center justify-content-center text-center ml-10 mr-10'>
          <div>
            <CalendarSvg width={isMobile ? 71 : 142} height={isMobile ? 74 : 149} outlined />
          </div>
          <div style={{ fontSize: isMobile ? 20 : 24, color: '#04333B', fontWeight: 600, marginTop: isMobile ? 20 : 48 }}>
            {completedInspectionsStats?.length === 0 ? 'No data available' : weekDays[maxDayIndex]}
          </div>
          <div style={{ fontSize: isMobile ? 12 : 16, color: '#04333B', fontWeight: 400, marginTop: isMobile ? 4 : 8, whiteSpace: 'nowrap' }}>
            {completedInspectionsStats?.length === 0 ? 'Complete inspections to see which' : 'Most Popular Day'}
          </div>
          <div style={{ fontSize: isMobile ? 12 : 16, color: '#04333B', fontWeight: 400, marginTop: isMobile ? 4 : 0, whiteSpace: 'nowrap' }}>
            {completedInspectionsStats?.length === 0 ? 'day is the most popular for your team' : 'Inspections Completed'}
          </div>
        </div>
      }
      {loadingCompletedInspectionsStats &&
        <div className='absolute flex left-0 right-0 top-0 bottom-0 items-center justify-center'>
          <LoadingIndicatorSvg />
        </div>
      }
    </div >
  )
}
